import type { DeleteSummary } from '../../../types/FileManagerTypes';

const pluralize = (count: number, singular: string) => (count === 1 ? singular : `${singular}s`);

/**
 * Confirmation copy for the trash summary modal, built from server counts.
 *
 * - Folders-free selections need no breakdown (totalItemCount === totalFileCount).
 * - Otherwise the "including" clause reports top-level folders and the full
 *   recursive file tally, e.g. "3 items including 2 folders and 17 files".
 */
export function buildDeleteSummaryMessage(summary: DeleteSummary): string {
  const { totalItemCount, folderCount, totalFileCount } = summary;
  const itemLabel = pluralize(totalItemCount, 'item');
  const suffix = 'These items will be moved to Trash.';

  if (folderCount === 0) {
    return `You are about to delete ${totalItemCount} ${itemLabel}. ${suffix}`;
  }

  const parts = [`${folderCount} ${pluralize(folderCount, 'folder')}`];
  if (totalFileCount > 0) {
    parts.push(`${totalFileCount} ${pluralize(totalFileCount, 'file')}`);
  }

  return `You are about to delete ${totalItemCount} ${itemLabel} including ${parts.join(' and ')}. ${suffix}`;
}

/** Named items shown in the map-usage warning before it falls back to "and N more". */
const MAX_LISTED_AFFECTED_ITEMS = 3;

/**
 * Map-association warning copy. Names the affected items when the server
 * supplied them, so the user knows which layers a delete would strip.
 */
export function buildMapUsageMessage(summary: DeleteSummary): string {
  const suffix = 'Deleting will remove the associated layers from those maps. Are you sure you want to proceed?';

  const names = summary.mapUsage.affectedItems.map((item) => item.name).filter(Boolean);
  if (names.length === 0) {
    return `Please note that one or more of the selected items are currently used in one or more maps. ${suffix}`;
  }

  const listed = names.slice(0, MAX_LISTED_AFFECTED_ITEMS).join(', ');
  const remaining = names.length - MAX_LISTED_AFFECTED_ITEMS;
  const subject =
    remaining > 0 ? `${listed} and ${remaining} more ${pluralize(remaining, 'item')} are` : `${listed} ${names.length === 1 ? 'is' : 'are'}`;

  return `Please note that ${subject} currently used in one or more maps. ${suffix}`;
}
